import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "../../lib/cn";

/**
 * StudioItem — DRY row for Studio drawer (used by Modes / Brand / Voice).
 *
 * One layout, three avatar tints (orange / amber / green) driven by
 * `kind`. Active rows get the orange-soft pill + check; non-active rows
 * show the scope badge (USER/ORG/GLOBAL) right-aligned.
 */
export type StudioItemKind = "mode" | "brand" | "voice";
export type StudioItemScope = "user" | "org" | "global";

export interface StudioItemProps extends React.HTMLAttributes<HTMLButtonElement> {
  kind: StudioItemKind;
  /** Avatar — initial letter (e.g. "A") or icon node. */
  avatar: React.ReactNode;
  name: string;
  description?: string;
  scope: StudioItemScope;
  active?: boolean;
  onSelect?: () => void;
}

const AVATAR_BG: Record<StudioItemKind, string> = {
  mode: "bg-ando-orange-soft text-ando-orange",
  brand: "bg-amber-500/15 text-amber-700",
  voice: "bg-success/15 text-success",
};

const SCOPE_LABEL: Record<StudioItemScope, string> = {
  user: "USER",
  org: "ORG",
  global: "GLOBAL",
};

export const StudioItem = React.forwardRef<HTMLButtonElement, StudioItemProps>(
  (
    { kind, avatar, name, description, scope, active, onSelect, className, ...rest },
    ref,
  ) => (
    <button
      ref={ref}
      type="button"
      onClick={onSelect}
      data-active={active ? "true" : undefined}
      className={cn(
        "group flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors",
        "hover:bg-black/[0.035]",
        active && "bg-ando-orange-soft hover:bg-ando-orange-soft",
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden
        className={cn(
          "inline-flex h-7 w-7 items-center justify-center rounded-md font-semibold text-[11px]",
          AVATAR_BG[kind],
        )}
      >
        {avatar}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-[12.5px] text-ink-1 leading-tight">
          {name}
        </span>
        {description && (
          <span className="mt-0.5 block truncate text-[10.5px] text-ink-3 leading-snug">
            {description}
          </span>
        )}
      </span>
      {active ? (
        <span
          aria-label="Attivo"
          className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-ando-orange text-white"
        >
          <Check size={9} strokeWidth={3} />
        </span>
      ) : (
        <span className="rounded bg-black/[0.04] px-1.5 py-0.5 font-mono text-[9px] text-ink-3 tracking-wider">
          {SCOPE_LABEL[scope]}
        </span>
      )}
    </button>
  ),
);

StudioItem.displayName = "StudioItem";
